import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, X, Clock, User, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import {
  workflowRunsApi,
  type WorkflowRunStep,
  type WorkflowTranscriptMessage,
} from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { RunStatusBadge } from "@/components/agent/agent-workflows-tab";
import {
  nodeTheme,
  runStatusStyles,
  summarizeOutputs,
} from "@/components/workflows/node-theme";
import { cn } from "@/lib/utils";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

/**
 * Per-run timeline view. A flowing, colour-coded audit of a workflow
 * execution that mirrors the canvas node language (trigger=green,
 * agent=blue, condition=amber, action=purple). Polls every 3s while live.
 */
export function WorkflowRunDetail() {
  const { workflowId, runId } = useParams<{
    workflowId: string;
    runId: string;
  }>();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.workflowRuns.detail(runId ?? ""),
    queryFn: () => workflowRunsApi.get(runId!),
    enabled: !!runId,
    refetchInterval: (q) => {
      const status = q.state.data?.run.status;
      return status === "running" || status === "awaiting" ? 3_000 : false;
    },
  });

  const cancel = useMutation({
    mutationFn: () => workflowRunsApi.cancel(runId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowRuns.detail(runId ?? ""),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflowRuns.byWorkflow(workflowId ?? ""),
      });
    },
  });

  if (!runId) return null;
  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={18} className="animate-spin text-text-muted" />
      </div>
    );
  }

  const { run, workflow, steps, assignedAgent, transcript } = data;
  const canCancel =
    run.status !== "completed" &&
    run.status !== "failed" &&
    run.status !== "cancelled";

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2">
        <button
          onClick={() =>
            navigate(
              withWorkspaceContext(
                `/tenants/${tenantId}/workflows/${workflowId}/runs`,
                searchParams,
              ),
            )
          }
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          aria-label="Back to runs"
        >
          <ArrowLeft size={14} />
        </button>
        <div className="flex flex-1 items-center gap-2 text-sm">
          <span className="font-semibold text-text-primary">
            {workflow?.name ?? "Workflow"}
          </span>
          <span className="text-text-muted">/ run</span>
          <span className="font-mono text-xs text-text-muted">
            {run.id.slice(0, 8)}
          </span>
          <RunStatusBadge status={run.status} />
        </div>
        {canCancel && (
          <Button
            size="sm"
            variant="outline"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            className="gap-1.5"
          >
            <X size={12} />
            Cancel
          </Button>
        )}
      </div>

      {/* ── Body (centred, readable column) ───────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-5">
          {/* Summary chips */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <Meta icon={<Zap size={12} className="text-green-600" />} label="Trigger" value={run.triggerSource ?? "manual"} />
            <Meta icon={<User size={12} className="text-accent" />} label="Agent" value={assignedAgent?.name ?? "—"} />
            <Meta icon={<Clock size={12} className="text-text-muted" />} label="Started" value={new Date(run.startedAt).toLocaleString()} />
            <Meta label="Duration" value={formatDuration(run.startedAt, run.completedAt)} />
            {run.completedAt && (
              <Meta label="Completed" value={new Date(run.completedAt).toLocaleTimeString()} />
            )}
            {run.status === "awaiting" && (
              <Meta label="Awaiting" value={run.awaitingType ?? "event"} />
            )}
          </div>

          {/* Retry / error banners */}
          {run.status === "running" && run.retryAt && (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              A step failed transiently — retry scheduled for{" "}
              {new Date(run.retryAt).toLocaleTimeString()}.
            </div>
          )}
          {run.error && (
            <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                Run failed
              </p>
              <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-300">
                {run.error.message}
                {run.error.stepId ? ` · step ${run.error.stepId}` : ""}
                {run.error.attempts ? ` · ${run.error.attempts} attempt(s)` : ""}
              </p>
            </div>
          )}

          {/* ── Step timeline ─────────────────────────────────────── */}
          <ol className="mt-5">
            {steps.length === 0 && (
              <li className="text-xs text-text-muted">
                No steps recorded yet — the agent hasn't started.
              </li>
            )}
            {steps.map((step, i) => (
              <StepRow
                key={step.id}
                step={step}
                isLast={i === steps.length - 1}
                isCurrent={
                  run.currentStepId === step.stepId &&
                  step.status !== "completed"
                }
              />
            ))}
          </ol>

          {/* ── Agent transcript ──────────────────────────────────── */}
          {transcript && transcript.length > 0 && (
            <section className="mt-8">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                Agent transcript
              </h3>
              <div className="space-y-2">
                {transcript.map((m, i) => (
                  <TranscriptRow key={i} message={m} />
                ))}
              </div>
              <p className="mt-2 text-[10px] text-text-muted">
                Wake directives + agent replies for this run. Deeper tool-call
                traces live in the agent's session log on its VM.
              </p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </span>
      <span className="text-text-primary">{value}</span>
    </div>
  );
}

function StepRow({
  step,
  isLast,
  isCurrent,
}: {
  step: WorkflowRunStep;
  isLast: boolean;
  isCurrent: boolean;
}) {
  const theme = nodeTheme(step.nodeType);
  const sstyle = runStatusStyles(step.status);
  const Icon = theme.Icon;
  const headline = summarizeOutputs(step.outputs);
  const branchLabel =
    typeof step.outputs.choiceLabel === "string"
      ? step.outputs.choiceLabel
      : step.branchTaken;
  const duration =
    step.durationMs != null
      ? step.durationMs < 1000
        ? `${step.durationMs}ms`
        : `${(step.durationMs / 1000).toFixed(1)}s`
      : null;

  return (
    <li className="relative flex gap-3 pb-3 last:pb-0">
      {/* Rail: status dot + connector */}
      <div className="relative flex w-5 shrink-0 justify-center">
        {!isLast && (
          <span className="absolute left-1/2 top-3 h-full w-px -translate-x-1/2 bg-border" />
        )}
        <span
          className={cn(
            "z-10 mt-1.5 h-3 w-3 rounded-full ring-4 ring-background",
            sstyle.dot,
            sstyle.pulse && "animate-pulse",
          )}
        />
      </div>

      {/* Card */}
      <div
        className={cn(
          "min-w-0 flex-1 rounded-xl border bg-surface shadow-sm transition-colors",
          isCurrent ? theme.border : "border-border",
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2">
          <span
            className={cn(
              "flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
              theme.tint,
              theme.text,
            )}
          >
            <Icon size={10} />
            {theme.label}
          </span>
          <span className="truncate text-[13px] font-semibold text-text-primary">
            {step.nodeLabel || step.stepId}
          </span>
          {branchLabel && (
            <span className="rounded bg-zinc-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-secondary">
              → {branchLabel}
            </span>
          )}
          {step.attempt > 1 && (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
              execution {step.attempt}
            </span>
          )}
          <span
            className={cn(
              "ml-auto rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide",
              sstyle.tint,
              sstyle.text,
            )}
          >
            {step.status}
          </span>
          {duration && (
            <span className="text-[11px] tabular-nums text-text-muted">
              {duration}
            </span>
          )}
        </div>

        {/* Headline output (prose) */}
        {headline && (
          <p className="border-t border-border/60 px-3 py-2 text-xs leading-relaxed text-text-secondary">
            {headline}
          </p>
        )}

        {/* Resume context */}
        {step.resumedAt && (
          <p className="px-3 pb-1 text-[11px] text-text-muted">
            Resumed {new Date(step.resumedAt).toLocaleString()}
            {step.resumeSource ? ` · ${step.resumeSource}` : ""}
          </p>
        )}

        {/* Error */}
        {step.error && (
          <p className="border-t border-rose-500/20 bg-rose-500/5 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
            {step.error.reason ?? JSON.stringify(step.error)}
          </p>
        )}

        {/* Raw details */}
        <details className="group border-t border-border/60">
          <summary className="cursor-pointer list-none px-3 py-1.5 text-[11px] text-text-muted hover:text-text-primary">
            <span className="group-open:hidden">▸ details</span>
            <span className="hidden group-open:inline">▾ details</span>
          </summary>
          <div className="grid grid-cols-1 gap-2 px-3 pb-2.5 md:grid-cols-2">
            <PayloadBlock title="Inputs" payload={step.inputs} />
            <PayloadBlock title="Outputs" payload={step.outputs} />
            {step.nodeSnapshot && (
              <div className="md:col-span-2">
                <PayloadBlock title="Node config (at run time)" payload={step.nodeSnapshot} />
              </div>
            )}
          </div>
        </details>
      </div>
    </li>
  );
}

function PayloadBlock({
  title,
  payload,
}: {
  title: string;
  payload: Record<string, unknown>;
}) {
  return (
    <div className="rounded-lg bg-background p-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {title}
      </p>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] text-text-secondary">
        {JSON.stringify(payload ?? {}, null, 2)}
      </pre>
    </div>
  );
}

function TranscriptRow({ message }: { message: WorkflowTranscriptMessage }) {
  const isUser = message.role === "user";
  const usage = (message.metadata?.usage ?? null) as
    | { input_tokens?: number; output_tokens?: number }
    | null;
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2",
        isUser ? "border-border bg-background" : "border-accent/20 bg-accent/5",
      )}
    >
      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-text-muted">
        <span>{isUser ? "platform → agent" : "agent"}</span>
        {message.metadata?.model && (
          <span className="font-mono normal-case">{message.metadata.model}</span>
        )}
        {usage?.output_tokens != null && (
          <span className="normal-case">
            {usage.input_tokens ?? "?"}→{usage.output_tokens} tok
          </span>
        )}
        <span className="ml-auto normal-case">
          {new Date(message.createdAt).toLocaleTimeString()}
        </span>
      </div>
      <p className="whitespace-pre-wrap break-words text-xs text-text-secondary">
        {message.content}
      </p>
    </div>
  );
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) {
    const mins = Math.floor(ms / 60_000);
    const secs = Math.floor((ms % 60_000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  return `${hrs}h ${mins}m`;
}
